import { useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { useState } from "react";
import { LoadingSpinner } from "../../../shared/ui/components/LoadingSpinner";
import { ProjectCard } from "./ProjectCard";
import { CreateProjectModal } from "./CreateProjectModal";
import { FolderIcon, PlusIcon } from "@heroicons/react/24/outline";

export function ProjectsListPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<"active" | "archived" | "completed">("active");

  const projects = useQuery(api.projects.getUserProjects, { status: statusFilter });

  if (projects === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 uppercase tracking-tight">Projects</h1>
            <p className="mt-2 text-sm text-gray-600">
              Organize your design exploration and create style guides with AI assistance
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            New Project
          </button>
        </div>

        {/* Status Filter Tabs */}
        <div className="mt-6 border-b-2 border-gray-300">
          <nav className="-mb-px flex space-x-8">
            {[
              { value: "active", label: "Active" },
              { value: "archived", label: "Archived" },
              { value: "completed", label: "Completed" },
            ].map((tab) => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value as typeof statusFilter)}
                className={`
                  whitespace-nowrap py-4 px-1 border-b-4 font-bold text-sm transition-colors uppercase tracking-wide
                  ${
                    statusFilter === tab.value
                      ? "border-gray-800 text-gray-900"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-400"
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-12">
          <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No projects</h3>
          <p className="mt-1 text-sm text-gray-500">
            {statusFilter === "active"
              ? "Get started by creating a new project"
              : `No ${statusFilter} projects found`}
          </p>
          {statusFilter === "active" && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn px-4 py-2 bg-yellow-300 text-black font-bold uppercase hover:bg-yellow-400 border-yellow-300 hover:border-yellow-400"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Project
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard key={project._id} project={project} />
          ))}
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && (
        <CreateProjectModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Keyboard Shortcuts Help */}
      <div className="mt-8 p-6 bg-gray-800 rounded-lg border-2 border-gray-800 shadow-comic">
        <h3 className="text-sm font-bold text-yellow-300 mb-3 uppercase tracking-wide">Keyboard Shortcuts</h3>
        <div className="grid grid-cols-2 gap-3 text-xs text-white font-medium">
          <div><kbd className="px-2 py-1 bg-gray-900 text-yellow-300 border-2 border-black rounded text-xs font-mono font-bold min-w-[32px] text-center">n</kbd> New project</div>
          <div><kbd className="px-2 py-1 bg-gray-900 text-yellow-300 border-2 border-black rounded text-xs font-mono font-bold min-w-[32px] text-center">h</kbd> Home</div>
        </div>
      </div>
    </div>
  );
}
